import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Button } from "../design/Button";
import { Modal } from "../design/Modal";
import { MAX_AVATAR_PIXELS } from "@/lib/upload-limits";

// Square avatar cropper: drag to position, slider to zoom, returns a
// JPEG-encoded square crop as { mimeType, content (base64, no data: prefix) }.

const VIEW = 280;
const OUTPUT = 512;

export interface CropResult {
  mimeType: string;
  content: string;
}

/**
 * Why the cropper can refuse a file the picker already accepted.
 *
 * `decode`: the browser could not decode it at all (a renamed non-image, a corrupt file). Nothing is
 * written in that case — `apply` is gated on `nat` — but without this the dialog sits there inert: an
 * empty circle, a dead Apply button and no reason given, so the user can only guess and cancel.
 *
 * `pixels`: it decodes but exceeds {@link MAX_AVATAR_PIXELS}. This is the bound that actually protects
 * the decode path, since bytes say nothing about how much memory a bitmap needs.
 */
export type AvatarRejection = "decode" | "pixels";

interface AvatarCropperProps {
  file: File | null;
  onCancel: () => void;
  onApply: (result: CropResult) => void;
  // Called instead of rendering a broken frame. The host is expected to close the cropper and say
  // why — a message left behind an open modal is a message nobody reads.
  onInvalid?: (reason: AvatarRejection) => void;
}

interface Natural {
  w: number;
  h: number;
}

interface Pos {
  x: number;
  y: number;
}

interface DragState extends Pos {
  px: number;
  py: number;
}

export function AvatarCropper({ file, onCancel, onApply, onInvalid }: AvatarCropperProps) {
  const intl = useIntl();
  const imgRef = useRef<HTMLImageElement | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const [nat, setNat] = useState<Natural | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState<Pos>({ x: 0, y: 0 });

  // Create + revoke the object URL in one effect so they stay paired.
  // Splitting creation (useMemo) from revocation (separate cleanup) leaves a
  // revoked URL after StrictMode's dev mount→cleanup→mount replay, so the crop
  // preview never loads.
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  const coverScale = nat ? Math.max(VIEW / nat.w, VIEW / nat.h) : 1;
  const effScale = coverScale * zoom;
  const dw = nat ? nat.w * effScale : 0;
  const dh = nat ? nat.h * effScale : 0;

  const clamp = (p: Pos, w: number, h: number): Pos => ({
    x: Math.min(0, Math.max(VIEW - w, p.x)),
    y: Math.min(0, Math.max(VIEW - h, p.y)),
  });

  function onImgLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const w = e.currentTarget.naturalWidth;
    const h = e.currentTarget.naturalHeight;
    // The byte cap cannot bound this: the decoded bitmap costs ~4 bytes per pixel regardless of how
    // well the file compressed, so the pixel count is what has to be checked — and onLoad is the first
    // moment it is known. Note this therefore rejects AFTER the browser has decoded the image once: it
    // protects the canvas draw and the sustained preview, not that first decode. Bounding that too
    // would mean createImageBitmap before mounting.
    if (w * h > MAX_AVATAR_PIXELS) {
      // Clear any previously accepted size too, so the cropper cannot stay "ready" with a stale one
      // if the host decides to leave the dialog open rather than close it.
      setNat(null);
      onInvalid?.("pixels");
      return;
    }
    setNat({ w, h });
    const cover = Math.max(VIEW / w, VIEW / h);
    const dw0 = w * cover;
    const dh0 = h * cover;
    setZoom(1);
    setPos({ x: (VIEW - dw0) / 2, y: (VIEW - dh0) / 2 });
  }

  function onZoom(next: number) {
    setZoom(next);
    if (nat) {
      const w = nat.w * coverScale * next;
      const h = nat.h * coverScale * next;
      setPos((p) => clamp(p, w, h));
    }
  }

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = { px: e.clientX, py: e.clientY, ...pos };
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!dragRef.current) return;
    const d = dragRef.current;
    setPos(clamp({ x: d.x + (e.clientX - d.px), y: d.y + (e.clientY - d.py) }, dw, dh));
  }

  function onPointerUp() {
    dragRef.current = null;
  }

  function apply() {
    const img = imgRef.current;
    /* v8 ignore next -- unreachable: the Apply button is `disabled={!nat}`, and `nat` is set only by the
       image's own load handler, so both the element and its intrinsic size are always present here */
    if (!img || !nat) return;
    const canvas = document.createElement("canvas");
    canvas.width = OUTPUT;
    canvas.height = OUTPUT;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const sx = -pos.x / effScale;
    const sy = -pos.y / effScale;
    const sSize = VIEW / effScale;
    ctx.drawImage(img, sx, sy, sSize, sSize, 0, 0, OUTPUT, OUTPUT);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    const content = dataUrl.slice(dataUrl.indexOf(",") + 1);
    onApply({ mimeType: "image/jpeg", content });
  }

  return (
    <Modal
      open={file != null}
      onClose={onCancel}
      title={intl.formatMessage({ id: "design.cropPhoto", defaultMessage: "Crop photo" })}
      maxWidth="22rem"
      footer={
        <>
          <Button
            variant="secondary"
            className="px-3 py-1 text-[10px]"
            onClick={onCancel}
          >
            {intl.formatMessage({ id: "common.cancel", defaultMessage: "Cancel" })}
          </Button>
          <Button
            variant="primary"
            className="px-3 py-1 text-[10px]"
            onClick={apply}
            disabled={!nat}
          >
            {intl.formatMessage({ id: "design.apply", defaultMessage: "Apply" })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col items-center gap-4">
        <div
          className="relative cursor-grab touch-none select-none overflow-hidden rounded-full bg-page ring-1 ring-ink/10 active:cursor-grabbing"
          style={{ width: VIEW, height: VIEW }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {url ? (
            <img
              ref={imgRef}
              src={url}
              alt=""
              draggable={false}
              onLoad={onImgLoad}
              onError={() => onInvalid?.("decode")}
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                width: dw,
                height: dh,
                transform: `translate(${pos.x}px, ${pos.y}px)`,
                maxWidth: "none",
              }}
            />
          ) : null}
        </div>
        <input
          type="range"
          min="1"
          max="3"
          step="0.01"
          value={zoom}
          onChange={(e) => onZoom(Number(e.target.value))}
          className="w-full accent-accent"
          aria-label={intl.formatMessage({ id: "design.zoom", defaultMessage: "Zoom" })}
        />
      </div>
    </Modal>
  );
}
